import { getParamByISO } from 'iso-country-currency';

import type { ClaimPolymorph } from '~/components/types/claim-types';
import SUPPORTED_CURRENCIES from '~/server_shared/generated-types/SUPPORTED_CURRENCIES';

/**
 * Currency options for dropdown/select components.
 * Maps supported currencies to { value, label } format.
 */
export const CURRENCY_OPTIONS = SUPPORTED_CURRENCIES.map((currency) => ({
  value: currency,
  label: currency,
}));

/**
 * Gets the primary/policy currency for an organization.
 * Defaults to 'USD' if not specified.
 *
 * @param claim - The claim object containing policy information
 * @returns Currency code (e.g., 'USD', 'EUR')
 */
export const getPolicyCurrency = (claim: ClaimPolymorph | undefined): string => {
  return claim?.policy?.policy_currency || 'USD'; // is there a organization default currency? todo for multi currency GA
};

/**
 * Merges policy currency with configured currencies, ensuring policy currency is always available.
 * Removes duplicates and sorts the result alphabetically.
 *
 * @param configuredCurrencies - Array of currencies from payment configuration
 * @param policyCurrency - The policy's currency that should always be available
 * @returns Sorted array of unique currencies with policy currency guaranteed to be included
 */
export const mergeWithPolicyCurrency = (configuredCurrencies: string[], policyCurrency: string): string[] => {
  if (!policyCurrency) {
    return configuredCurrencies || [];
  }

  if (!configuredCurrencies || configuredCurrencies.length === 0) {
    return [policyCurrency];
  }

  const currencySet = new Set([policyCurrency, ...configuredCurrencies]);
  return Array.from(currencySet).sort();
};

/**
 * Gets the currency code for a given country code.
 * Uses the iso-country-currency package to map ISO country codes to their default currency.
 *
 * @param countryCode - ISO country code (e.g., 'US', 'GB', 'DE')
 * @returns Currency code (e.g., 'USD', 'GBP', 'EUR') or undefined if not found
 */
export const getCurrencyFromCountry = (countryCode: string): string | undefined => {
  // Bulgaria EUR adoption (Jan 1, 2026)
  if (countryCode === 'BG') {
    return 'EUR';
  }

  try {
    return getParamByISO(countryCode, 'currency');
  } catch (error) {
    // If the package fails to get currency, return undefined
    // eslint-disable-next-line no-console
    console.warn(`Failed to get currency for country ${countryCode}:`, error);
    return undefined;
  }
};

/**
 * Formats an exchange rate for display.
 *
 * Uses 4 decimal places when |rate| >= 1, and 4 significant figures otherwise.
 * Without this, `toFixed(4)` collapses low-value rates (e.g. VND→AUD ~0.000053)
 * to "0.0001".
 */
export const formatExchangeRate = (rate: number | string | null | undefined): string => {
  const parsed = typeof rate === 'number' ? rate : parseFloat(rate ?? '');
  if (!Number.isFinite(parsed)) {
    return '';
  }
  if (parsed === 0 || Math.abs(parsed) >= 1) {
    return parsed.toFixed(4);
  }
  return parsed.toPrecision(4);
};

// Type definition for currency conversion parameters
interface ConvertAmountForLimitCheckParams {
  amount: number;
  requestCurrency?: string;
  claim?: ClaimPolymorph;
  conversionRate?: number;
}

/**
 * Convert payment amount to policy currency for limit comparisons.
 * This ensures that all limit checks compare amounts in the same currency.
 *
 * @param params - Object containing conversion parameters
 * @param params.amount - Original payment amount
 * @param params.requestCurrency - Currency of the payment request (optional)
 * @param params.claim - Claim object containing policy currency
 * @param params.conversionRate - Conversion rate from form values (optional)
 * @returns Amount converted to policy currency
 */
export const convertAmountForLimitCheck = ({
  amount,
  requestCurrency,
  claim,
  conversionRate,
}: ConvertAmountForLimitCheckParams): number => {
  const policyCurrency = getPolicyCurrency(claim);

  // If same currency or no conversion rate, return original amount
  if (!requestCurrency || requestCurrency === policyCurrency || !conversionRate) {
    return amount;
  }

  // Convert using the provided conversion rate
  return amount / conversionRate;
};
